import { FiBarChart2, FiLock, FiShield } from "solid-icons/fi";
import { createEffect, createSignal } from "solid-js";
import { useI18n } from "@/api/i18n";
import postHogClient from "@/api/posthog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Switch,
  SwitchControl,
  SwitchDescription,
  SwitchLabel,
  SwitchThumb,
} from "@/components/ui/switch";
import {
  closePreferences,
  consent,
  preferencesOpen,
  saveConsent,
} from "../store/consent-store";

const CARD_CLASS =
  "flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4";
const ICON_CHIP_CLASS =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg";

/**
 * "Personalizza" dialog with two categories: Analitici (toggle, off by default,
 * shown first) and Necessari (locked on). Opened from the banner or the footer via
 * the shared `preferencesOpen` state, so a single instance mounted at the router
 * root serves both.
 *
 * The initial toggle is derived from the current choice on every open:
 *  - a valid choice on record ⇒ prefilled with it (edit/re-entry);
 *  - no valid choice, incl. after a CONSENT_VERSION bump ⇒ OFF, never pre-checked
 *    (getStoredConsent returns null on a version mismatch, so `consent()` is null).
 *
 * A single full-width "Salva preferenze" CTA persists the toggle state: analytics
 * ON opts in, OFF opts out (also revokes a prior accept). No separate accept/reject
 * shortcuts — those live on the first-open banner.
 */
export function CookiePreferencesPanel() {
  const { t } = useI18n();
  const [analytics, setAnalytics] = createSignal(false);

  createEffect(() => {
    if (preferencesOpen()) {
      setAnalytics(consent()?.analytics === true);
    }
  });

  const save = () => {
    const enabled = analytics();
    saveConsent(enabled);
    if (enabled) {
      postHogClient.enableCapture();
    } else {
      postHogClient.disableCapture();
    }
    closePreferences();
  };

  return (
    <Dialog
      open={preferencesOpen()}
      onOpenChange={(isOpen) => {
        if (!isOpen) closePreferences();
      }}
    >
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("cookie_consent.panel.title")}</DialogTitle>
          <DialogDescription>
            {t("cookie_consent.panel.description")}
          </DialogDescription>
        </DialogHeader>

        <div class="flex flex-col gap-3">
          {/* Analitici — user-controlled toggle, off by default (shown first) */}
          <Switch
            checked={analytics()}
            onChange={setAnalytics}
            class={CARD_CLASS}
          >
            <div class="flex items-start gap-3">
              <span
                class={`${ICON_CHIP_CLASS} bg-primary/10 text-primary`}
                aria-hidden="true"
              >
                <FiBarChart2 class="h-5 w-5" />
              </span>
              <div class="flex flex-col gap-1">
                <SwitchLabel class="text-sm font-medium text-gray-900">
                  {t("cookie_consent.panel.analytics.title")}
                </SwitchLabel>
                <SwitchDescription class="text-sm text-gray-600">
                  {t("cookie_consent.panel.analytics.description")}
                </SwitchDescription>
              </div>
            </div>
            <SwitchControl>
              <SwitchThumb />
            </SwitchControl>
          </Switch>

          {/* Necessari — always active, cannot be turned off */}
          <Switch checked disabled class={CARD_CLASS}>
            <div class="flex items-start gap-3">
              <span
                class={`${ICON_CHIP_CLASS} bg-gray-100 text-gray-500`}
                aria-hidden="true"
              >
                <FiShield class="h-5 w-5" />
              </span>
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <SwitchLabel class="text-sm font-medium text-gray-900">
                    {t("cookie_consent.panel.necessary.title")}
                  </SwitchLabel>
                  <span class="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                    {t("cookie_consent.panel.necessary.always_on")}
                  </span>
                </div>
                <SwitchDescription class="text-sm text-gray-600">
                  {t("cookie_consent.panel.necessary.description")}
                </SwitchDescription>
              </div>
            </div>
            <SwitchControl>
              <SwitchThumb />
            </SwitchControl>
          </Switch>
        </div>

        {/* Reassurance: aggregate analytics, data is never sold */}
        <div class="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-500">
          <FiLock class="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t("cookie_consent.panel.no_sale")}</span>
        </div>

        <DialogFooter>
          <Button variant="default" class="w-full" onClick={save}>
            {t("cookie_consent.panel.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
